import { isAxiosError } from 'axios';

import type { AIBudgetStatus } from '@/hooks/useAIBudget';

/**
 * BEGINNING gate predicate: is the tenant's monthly AI budget fully consumed?
 *
 * Fail OPEN only on genuine uncertainty / no-cap states; otherwise agree with
 * the AISpendMeter, which renders a configured $0 cap as 100%/exhausted. The
 * END-guard 402 remains the backstop if the budget was actually exhausted but
 * the status hadn't loaded:
 *   - undefined  -> loading / not yet fetched  -> false (never block on uncertainty)
 *   - null       -> provisioned, no budget configured -> false (unprovisioned != exhausted)
 *   - !set       -> no monthly limit -> false (no cap can't be maxed)
 *   - set && max_limit<=0 -> configured $0 cap -> TRUE (Bifrost enforces $0 as
 *     fully blocking; the only fail-open cases are !budget / !set)
 *   - set && max_limit>0 && current_usage >= max_limit -> true (block + show modal)
 *
 * Threshold is exactly 100% (`>=`), distinct from the AISpendCard's `overLimit`
 * (`>`) — do not conflate the two predicates.
 */
export function isBudgetExhausted(
  budget: AIBudgetStatus | null | undefined
): boolean {
  if (!budget || !budget.set) return false; // no budget / no cap -> fail open
  if (budget.max_limit <= 0) return true; // configured $0 cap = blocked, not unlimited
  return budget.current_usage >= budget.max_limit;
}

/**
 * END guard predicate: is this error the backend's HTTP 402 budget rejection?
 * The backend refuses new hunts at the >=90% reserve threshold
 * (BudgetThresholdPercent = 90) — the platform reserves the final ~10% of the
 * cap, so the 402 fires regardless of the next hunt's projected cost. It is the
 * authoritative signal that the budget threshold for starting new hunts has been
 * reached. Non-axios errors and other status codes return false so the default
 * error handling is left untouched.
 */
export function is402(err: unknown): boolean {
  if (isAxiosError(err) && err.response?.status === 402) {
    return true;
  }
  return (err as { status?: number } | null | undefined)?.status === 402;
}
